import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Minus, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, getImageUrl } from "../api";
import { useStore } from "../store";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useStore();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [activeImg, setActiveImg] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setActiveImg(0);
  }, [id]);

  useEffect(() => {
    setLoading(true);
    setActiveImg(0);
    api.get(`/products/${id}`)
      .then((r) => setProduct(r.data))
      .catch(() => setProduct(null))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-10 animate-pulse" data-testid="product-loading">
        <div className="aspect-[3/4] bg-[#F0EBE2] w-full sm:w-1/2" />
        <div className="h-6 bg-[#F0EBE2] mt-6 w-48" />
        <div className="h-4 bg-[#F0EBE2] mt-3 w-24" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="py-24 text-center" data-testid="product-not-found">
        <p className="font-display text-2xl text-[#8B5A2B]">Capo non trovato</p>
        <button data-testid="back-button" onClick={() => navigate(-1)} className="mt-6 text-sm tracking-wide-spaced uppercase text-[#AA7C11] underline underline-offset-4">
          Torna indietro
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto" data-testid="product-page">
      <div className="flex items-center gap-3 px-4 pt-5">
        <button
          data-testid="back-button"
          onClick={() => navigate(-1)}
          aria-label="Torna indietro"
          className="p-2 -ml-2 text-[#2C2C2C] hover:opacity-60 transition-opacity duration-200"
        >
          <ArrowLeft size={20} strokeWidth={1.5} />
        </button>
      </div>
      <div className="sm:grid sm:grid-cols-2 sm:gap-10 px-4 pb-24">
        <div className="mt-2">
          <div className="bg-white overflow-hidden">
            <img
              src={getImageUrl((product.images?.length ? product.images : [product.image_url])[activeImg] || product.image_url)}
              alt={product.name}
              className="w-full aspect-[3/4] object-cover"
              data-testid="product-image"
            />
          </div>
          {product.images?.length > 1 && (
            <div className="flex gap-2 mt-3 overflow-x-auto no-scrollbar" data-testid="product-gallery">
              {product.images.map((img, i) => (
                <button
                  key={i}
                  type="button"
                  data-testid={`gallery-thumb-${i}`}
                  onClick={() => setActiveImg(i)}
                  className={`shrink-0 border transition-opacity duration-200 ${i === activeImg ? "border-[#D4AF37] opacity-100" : "border-transparent opacity-60"}`}
                >
                  <img src={getImageUrl(img)} alt={`${product.name} ${i + 1}`} className="w-16 h-20 object-cover bg-white" />
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="pt-8 sm:pt-4 text-center sm:text-left">
          {product.best_seller && (
            <span data-testid="best-seller-badge" className="inline-block mb-3 gold-gradient text-white text-[10px] font-semibold tracking-wide-spaced uppercase px-3 py-1.5">
              Best seller
            </span>
          )}
          <h1 className="font-display text-3xl text-[#2C2C2C]" data-testid="product-name">{product.name}</h1>
          <p className="mt-3 text-lg font-semibold gold-gradient-text" data-testid="product-price">€ {product.price.toFixed(2)}</p>
          {product.description && (
            <p className="mt-5 text-sm leading-relaxed text-[#6B6357] tracking-wide" data-testid="product-description">{product.description}</p>
          )}

          <div className="mt-8 flex items-center justify-center sm:justify-start gap-5">
            <div className="flex items-center border border-[#EAE4DA]">
              <button data-testid="qty-minus" onClick={() => setQty((q) => Math.max(1, q - 1))} className="p-3 text-[#2C2C2C]" aria-label="Riduci quantità">
                <Minus size={15} strokeWidth={1.5} />
              </button>
              <span className="w-10 text-center text-sm font-medium" data-testid="qty-value">{qty}</span>
              <button data-testid="qty-plus" onClick={() => setQty((q) => q + 1)} className="p-3 text-[#2C2C2C]" aria-label="Aumenta quantità">
                <Plus size={15} strokeWidth={1.5} />
              </button>
            </div>
          </div>

          {product.sold_out ? (
            <p data-testid="sold-out-label" className="mt-6 w-full sm:w-auto bg-[#2C2C2C] text-white px-12 py-4 text-[12px] font-semibold tracking-wide-spaced uppercase text-center">
              Esaurito
            </p>
          ) : (
            <button
              data-testid="add-to-cart-button"
              onClick={() => {
                addToCart(product, qty);
                toast.success("Aggiunto al carrello", { position: "top-center" });
              }}
              className="mt-6 w-full sm:w-auto gold-gradient text-white px-12 py-4 text-[12px] font-semibold tracking-wide-spaced uppercase hover:opacity-90 transition-opacity duration-300"
            >
              Aggiungi al carrello
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
